/**
 * Sessions API Hooks - Sophisticated pattern with TanStack Query
 *
 * Provides type-safe API operations for Sessions with proper error handling,
 * caching, and validation following the established architecture pattern.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import type {
  SessionCreate,
  SessionCreateInput,
  SessionFilters,
  SessionListResponse,
  SessionResponse,
  SessionUpdate,
  SessionUpdateInput,
} from "@/types/session"
import { unwrapStandardResponse } from "@/utils/apiResponse"

import { useApiMutation } from "./useApiMutation"

/**
 * Fetch paginated list of sessions with permission checks
 */
export function useSessions(filters: SessionFilters = {}) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["sessions", filters],
    queryFn: async (): Promise<SessionListResponse> => {
      const response = await api.sessions.readSessionsApiV1SessionsGet({
        limit: filters.limit,
        skip: filters.skip,
        search: filters.search,
        sortBy: filters.sort_by,
        sortOrder: filters.sort_order,
        createdBy: filters.created_by,
        createdAfter: filters.created_after,
        createdBefore: filters.created_before,
        updatedAfter: filters.updated_after,
        updatedBefore: filters.updated_before,
      })

      return unwrapStandardResponse<SessionListResponse>(
        response,
      ) as SessionListResponse
    },
    enabled: hasPermission("sessions:read"),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch single session by ID with permission checks
 */
export function useSession(sessionId: string) {
  const { hasPermission } = usePermissions()

  return useQuery({
    queryKey: ["sessions", sessionId],
    queryFn: async (): Promise<SessionResponse> => {
      const response = await api.sessions.readSessionApiV1SessionsSessionIdGet({
        sessionId: sessionId,
      })

      return unwrapStandardResponse<SessionResponse>(
        response,
      ) as SessionResponse
    },
    enabled: !!sessionId && hasPermission("sessions:read"),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Create new session with validation and error handling
 */
export function useCreateSession() {
  return useApiMutation<SessionResponse, unknown, SessionCreateInput>({
    mutationFn: async (sessionData) => {
      // Let the backend handle permission checks

      const sessionCreate: SessionCreate = {
        name: sessionData.name,
        slug: sessionData.slug,
        description: sessionData.description || null,
        session_type: sessionData.session_type || null,
        start_week: sessionData.start_week,
        end_week: sessionData.end_week,
        total_classes: sessionData.total_classes ?? 0,
        registration_opens: sessionData.registration_opens || null,
        registration_closes: sessionData.registration_closes || null,
        early_bird_deadline: sessionData.early_bird_deadline || null,
        makeup_classes_allowed: sessionData.makeup_classes_allowed ?? null,
        no_class_dates: sessionData.no_class_dates ?? null,
        is_active: sessionData.is_active ?? null,
        is_published: sessionData.is_published ?? null,
        notes: sessionData.notes || null,
      }

      const response = await api.sessions.createSessionApiV1SessionsPost({
        requestBody: sessionCreate,
      })

      return unwrapStandardResponse<SessionResponse>(
        response,
      ) as SessionResponse
    },
    invalidateQueries: [["sessions"]],
    successToast: (data) => ({
      title: "Session created successfully",
      description: ` "${data.id}" has been created.`,
    }),
  })
}

/**
 * Update existing session with validation and error handling
 */
export function useUpdateSession() {
  return useApiMutation<
    SessionResponse,
    unknown,
    { sessionId: string; sessionData: SessionUpdateInput }
  >({
    mutationFn: async ({
      sessionId,
      sessionData,
    }: {
      sessionId: string
      sessionData: SessionUpdateInput
    }) => {
      // Let the backend handle permission checks

      const sessionUpdate: SessionUpdate = {
        name: sessionData.name,
        slug: sessionData.slug,
        description: sessionData.description || null,
        session_type: sessionData.session_type || null,
        start_week: sessionData.start_week,
        end_week: sessionData.end_week,
        total_classes: sessionData.total_classes ?? undefined,
        registration_opens: sessionData.registration_opens || null,
        registration_closes: sessionData.registration_closes || null,
        early_bird_deadline: sessionData.early_bird_deadline || null,
        makeup_classes_allowed: sessionData.makeup_classes_allowed,
        no_class_dates: sessionData.no_class_dates,
        is_active: sessionData.is_active,
        is_published: sessionData.is_published,
        notes: sessionData.notes || null,
      }

      const response =
        await api.sessions.updateSessionApiV1SessionsSessionIdPut({
          sessionId: sessionId,
          requestBody: sessionUpdate,
        })

      return unwrapStandardResponse<SessionResponse>(
        response,
      ) as SessionResponse
    },
    setQueryData: [
      {
        queryKey: (variables) => ["sessions", variables.sessionId],
        updater: (_previous, data) => data,
      },
    ],
    invalidateQueries: [["sessions"]],
  })
}

/**
 * Delete session with permission checks and error handling
 */
export function useDeleteSession() {
  return useApiMutation<{ sessionId: string }, unknown, string>({
    mutationFn: async (sessionId) => {
      // Let the backend handle permission checks
      await api.sessions.deleteSessionApiV1SessionsSessionIdDelete({
        sessionId: sessionId,
      })
      return { sessionId }
    },
    removeQueries: [(variables) => ["sessions", variables]],
    invalidateQueries: [["sessions"]],
  })
}

/**
 * Invalidate sessions queries to force a refetch
 */
export function useInvalidateSessions() {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries({ queryKey: ["sessions"] })
  }
}
